const { oldColors, baseColors, semanticColors } = require('./vtmn-colors.json');
const { vtmnPlayColors } = require('./vtmn-play-colors.json');

/** @type {import('tailwindcss').Config} */
module.exports = {
  important: false,
  content: ['./src/**/*.{js,jsx,ts,tsx,scss,css,html}'],
  options: {
    safelist: {
      standard: [/^op\-/], // allow onepay related class (prefixed by op-*)
    },
  },

  plugins: [
    require('./tailwind-plugin/container'),
    require('./tailwind-plugin/column-count'),
  ],

  theme: {
    extend: {
      borderRadius: {
        DEFAULT: '8px',
        2: '2px',
        4: '4px',
        full: '50%',
        image: '12px',
        'vp-main': 'var(--vp-semantic-border-radius-main)',
        'vp-s': 'var(--vp-semantic-border-radius-s)',
        'vp-m': 'var(--vp-semantic-border-radius-m)',
        'vp-full': 'var(--vp-semantic-border-radius-full)',
      },

      inset: (theme, { negative }) => ({
        ...theme('spacing'),
        ...negative(theme('spacing')),
        'toasts-spacing-y': '0.875rem',
        'toasts-spacing-x': '1.875rem',
      }),

      height: (theme) => ({
        ...theme('spacing'),
        screen: '100vh',
        'activity-creation-photo': '300px',
        'form-icon': '30px',
        'form-input': '48px',
        'google-maps': '300px',
        'organizer-logo': '100px',
        'button-icon': '24px',
        'header-login-button': '50px',
        'card-image': '100px',
      }),

      maxWidth: (theme) => ({
        '2xl': theme('screens.2xl'),
        none: 'none',
      }),

      minWidth: (theme) => ({ ...theme('spacing') }),

      minHeight: (theme) => ({
        ...theme('spacing'),
        'live-card': '100px',
      }),

      maxHeight: {
        'form-list': '200px',
        none: 'none',
      },

      skew: {
        decathlon: '10deg',
        '-decathlon': '-10deg',
      },

      zIndex: {
        '-1': '-1',
        1: '1',
      },
    },

    backgroundColor: (theme) => ({
      ...theme('colors'),
      transparent: 'transparent',
      'vp-background': {
        main: 'var(--vp-semantic-color-background-main)',
        alternative: 'var(--vp-semantic-color-background-alternative)',
      },
      'vp-container': {
        brand: 'var(--vp-semantic-color-container-brand)',
        neutral: 'var(--vp-semantic-color-container-neutral)',
        quiet: 'var(--vp-semantic-color-container-quiet)',
        overlay: 'var(--vp-semantic-color-container-overlay)',
        catchy: 'var(--vp-semantic-color-container-catchy)',
        'commercial-catchy':
          'var(--vp-semantic-color-container-commercial-catchy)',
        'commercial-neutral':
          'var(--vp-semantic-color-container-commercial-neutral)',
      },
      'vp-alpha': {
        brand: {
          s: 'var(--vp-semantic-color-alpha-brand-s)',
          m: 'var(--vp-semantic-color-alpha-brand-m)',
          l: 'var(--vp-semantic-color-alpha-brand-l)',
        },
        quiet: {
          s: 'var(--vp-semantic-color-alpha-quiet-s)',
          m: 'var(--vp-semantic-color-alpha-quiet-m)',
        },
        neutral: {
          xs: 'var(--vp-semantic-color-alpha-neutral-xs)',
          s: 'var(--vp-semantic-color-alpha-neutral-s)',
          m: 'var(--vp-semantic-color-alpha-neutral-m)',
          l: 'var(--vp-semantic-color-alpha-neutral-l)',
        },
      },
      'vp-status': {
        positive: 'var(--vp-semantic-color-status-positive)',
        negative: 'var(--vp-semantic-color-status-negative)',
        warning: 'var(--vp-semantic-color-status-warning)',
        info: 'var(--vp-semantic-color-status-info)',
      },
    }),

    backgroundImage: (theme) => ({
      none: 'none',
      'image-overlay': `linear-gradient(
        180deg,
        rgba(41,51,62,0.8),
        rgba(41, 51, 62, 0.075) 30%,
        rgba(41, 51, 62, 0.025) 32.5%,
        rgba(41, 51, 62, 0) 35%
      )`,
      'light-black-gradient': `linear-gradient(
        8deg,
        rgba(0, 0, 0, 0.55) 31.3%,
        rgba(0, 0, 0, 0.00) 67.82%
      )`,
      'black-light-gradient': `linear-gradient(
        8deg,
        rgba(255, 255, 255, 0.55) 31.3%,
        rgba(0, 0, 0, 0.00) 67.82%
      )`,
      'blue-gradient': `linear-gradient(
        135deg,
        ${theme('colors.brand.DEFAULT')},
        ${theme('colors.brand.pro')}
      )`,
      'light-blue-gradient': `linear-gradient(
        158.92deg,
        ${theme('colors.brand.DEFAULT')},
        #244676 100%
      )`,
    }),

    borderColor: (theme) => ({
      ...theme('colors'),
      'vp-border': {
        brand: 'var(--vp-semantic-color-border-brand)',
        catchy: 'var(--vp-semantic-color-border-catchy)',
        neutral: 'var(--vp-semantic-color-border-neutral)',
        quiet: 'var(--vp-semantic-color-border-quiet)',
        inverse: 'var(--vp-semantic-color-border-inverse)',
      },
      'vp-status': {
        positive: 'var(--vp-semantic-color-status-positive)',
        negative: 'var(--vp-semantic-color-status-negative)',
        warning: 'var(--vp-semantic-color-status-warning)',
        info: 'var(--vp-semantic-color-status-info)',
      },
    }),

    borderWidth: {
      DEFAULT: '1px',
      0: '0',
      2: '2px',
      3: '3px',
      4: '4px',
      6: '6px',
      9: '9px',
    },

    boxShadow: (theme) => ({
      bottom: '0 5px 7px rgba(0, 0, 0, 0.125)',
      'bottom-light': '0 2px 4px rgba(0, 0, 0, 0.125)',
      'bottom-large': '0 10px 14px rgba(0, 0, 0, 0.125)',
      centered: '0 0px 7px rgba(0, 0, 0, 0.2)',
      'centered-light': '0 0px 4px rgba(0, 0, 0, 0.2)',
      'centered-large':
        '0px 0px 4px rgba(41, 51, 62, 0.05), 0px 0px 24px rgba(41, 51, 62, 0.25);',
      top: '0 -5px 7px rgba(0, 0, 0, 0.125)',
      'top-light': '0 -2px 4px rgba(0, 0, 0, 0.125)',
      'top-large': '0 -10px 14px rgba(0, 0, 0, 0.125)',
      focus: `0 0 0 2px ${theme('colors.blue-200')}`,
      'focus-grey': `0 0 0 2px ${theme('colors.grey.3')}`,
      none: 'none',
      'navigation-tab-bar-conversion-button': `4px 4px 1px 0px ${theme(
        'colors.grey.2'
      )}`,
    }),

    colors: {
      ...vtmnPlayColors,
      ...oldColors,
      ...baseColors,
      ...semanticColors,
      content: {
        'vp-brand': 'var(--vp-semantic-color-content-brand)',
        'vp-neutral': 'var(--vp-semantic-color-content-neutral)',
        'vp-quiet': 'var(--vp-semantic-color-content-quiet)',
        'vp-inverse': 'var(--vp-semantic-color-content-inverse)',
        'vp-on-brand-accent':
          'var(--vp-semantic-color-content-on-brand-accent)',
        'vp-on-brand-neutral':
          'var(--vp-semantic-color-content-on-brand-neutral)',
        'vp-on-media-accent':
          'var(--vp-semantic-color-content-on-media-accent)',
        'vp-on-media-neutral':
          'var(--vp-semantic-color-content-on-media-neutral)',
        'vp-on-commercial-catchy':
          'var(--vp-semantic-color-content-on-commercial-catchy)',
        'vp-on-commercial-neutral':
          'var(--vp-semantic-color-content-on-commercial-neutral)',
        'vp-logo': 'var(--vp-semantic-color-content-logo)',
        'vp-service': 'var(--vp-semantic-color-content-service)',
      },
      status: {
        'vp-positive': 'var(--vp-semantic-color-status-positive)',
        'vp-negative': 'var(--vp-semantic-color-status-negative)',
        'vp-warning': 'var(--vp-semantic-color-status-warning)',
        'vp-info': 'var(--vp-semantic-color-status-info)',
      },
    },

    divideColor: {
      'vp-border': {
        brand: 'var(--vp-semantic-color-border-brand)',
        catchy: 'var(--vp-semantic-color-border-catchy)',
        neutral: 'var(--vp-semantic-color-border-neutral)',
        quiet: 'var(--vp-semantic-color-border-quiet)',
        inverse: 'var(--vp-semantic-color-border-inverse)',
      },
      'vp-status': {
        positive: 'var(--vp-semantic-color-status-positive)',
        negative: 'var(--vp-semantic-color-status-negative)',
        warning: 'var(--vp-semantic-color-status-warning)',
        info: 'var(--vp-semantic-color-status-info)',
      },
    },
    stroke: {
      'vp-content': {
        brand: 'var(--vp-semantic-color-content-brand)',
        neutral: 'var(--vp-semantic-color-content-neutral)',
        quiet: 'var(--vp-semantic-color-content-quiet)',
        inverse: 'var(--vp-semantic-color-content-inverse)',
        'on-brand-accent': 'var(--vp-semantic-color-content-on-brand-accent)',
        'on-brand-neutral': 'var(--vp-semantic-color-content-on-brand-neutral)',
        'on-media-accent': 'var(--vp-semantic-color-content-on-media-accent)',
        'on-media-neutral': 'var(--vp-semantic-color-content-on-media-neutral)',
        'on-commercial-catchy':
          'var(--vp-semantic-color-content-on-commercial-catchy)',
        'on-commercial-neutral':
          'var(--vp-semantic-color-content-on-commercial-neutral)',
        logo: 'var(--vp-semantic-color-content-logo)',
        service: 'var(--vp-semantic-color-content-service)',
      },
      'vp-status': {
        positive: 'var(--vp-semantic-color-status-positive)',
        negative: 'var(--vp-semantic-color-status-negative)',
        warning: 'var(--vp-semantic-color-status-warning)',
        info: 'var(--vp-semantic-color-status-info)',
      },
    },

    fontFamily: {
      regular: [
        'Inter',
        'Helvetica Neue',
        'Arial',
        'sans-serif',
        'system-ui',
        '-apple-system',
      ],

      condensed: [
        'Inter',
        'serif',
        'Arial',
        'sans-serif',
        'system-ui',
        '-apple-system',
      ],
    },

    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '2rem',
      '4xl': '3rem',
      icon: '1.75rem',
      banner: '4.5rem',
      s: [
        'var(--vp-semantic-typography-screen-all-body-s-font-size)',
        {
          lineHeight:
            'var(--vp-semantic-typography-screen-all-body-s-line-height)',
          letterSpacing:
            'var(--vp-semantic-typography-screen-all-body-s-letter-spacing)',
          fontWeight:
            'var(--vp-semantic-typography-screen-all-body-s-font-weight)',
        },
      ],
      m: [
        'var(--vp-semantic-typography-screen-all-body-m-font-size)',
        {
          lineHeight:
            'var(--vp-semantic-typography-screen-all-body-m-line-height)',
          letterSpacing:
            'var(--vp-semantic-typography-screen-all-body-m-letter-spacing)',
          fontWeight:
            'var(--vp-semantic-typography-screen-all-body-m-font-weight)',
        },
      ],
      l: [
        'var(--vp-semantic-typography-screen-all-body-l-font-size)',
        {
          lineHeight:
            'var(--vp-semantic-typography-screen-all-body-l-line-height)',
          letterSpacing:
            'var(--vp-semantic-typography-screen-all-body-l-letter-spacing)',
          fontWeight:
            'var(--vp-semantic-typography-screen-all-body-l-font-weight)',
        },
      ],
    },

    letterSpacing: {
      condensed: '.24px',
    },

    // Keep ratio between font size & line height: 1.17
    // Example: font-size:1rem -> line-height:1.17rem
    lineHeight: {
      xs: '0.88rem',
      sm: '1.02rem',
      base: '1.17rem',
      lg: '1.32rem',
      xl: '1.46rem',
      '2xl': '1.76rem',
      '3xl': '2.34rem',
      '4xl': '3.51rem',
      'form-icon': '1.75rem',
      icon: '1.75rem',
      none: '1',
      banner: '4.5rem',
    },

    margin: (theme, { negative }) => ({
      ...theme('spacing'),
      ...negative(theme('spacing')),
      auto: 'auto',
      'timeline-item-separator': `35px 0 0 15px`,
    }),

    opacity: {
      0: '0',
      5: '0.05',
      10: '0.1',
      20: '0.2',
      50: '0.5',
      80: '0.8',
      85: '0.85',
      100: '1',
    },

    screens: {
      xs: '600px',
      sm: '601px',
      md: '900px',
      lg: '1302px',
      xl: '1688px',
      '2xl': '1884px',
    },

    spacing: {
      0: '0',
      1: '0.25rem',
      2: '0.5rem',
      3: '1rem',
      4: '1.5rem',
      5: '2rem',
      6: '2.5rem',
      7: '3rem',
      8: '4rem',
      9: '5rem',
      '9xl': 'var(--vp-core-spacing-900)',
      '8xl': 'var(--vp-core-spacing-850)',
      '7xl': 'var(--vp-core-spacing-800)',
      '6xl': 'var(--vp-core-spacing-700)',
      '5xl': 'var(--vp-core-spacing-650)',
      '4xl': 'var(--vp-core-spacing-600)',
      '3xl': 'var(--vp-core-spacing-500)',
      '2xl': 'var(--vp-core-spacing-400)',
      xl: 'var(--vp-core-spacing-300)',
      l: 'var(--vp-core-spacing-250)',
      m: 'var(--vp-core-spacing-200)',
      s: 'var(--vp-core-spacing-150)',
      xs: 'var(--vp-core-spacing-100)',
      '2xs': 'var(--vp-core-spacing-050)',
      '3xs': 'var(--vp-core-spacing-025)',
      '4xs': 'var(--vp-core-spacing-0)',
    },

    width: (theme) => ({
      ...theme('spacing'),
      'fit-content': 'fit-content',
      auto: 'auto',
      full: '100%',
      px: '1px',
      '2px': '2px',
      screen: '100vw',
      '1/2': '50%',
      '1/3': '33.333333%',
      '2/3': '66.666667%',
      '1/4': '25%',
      '2/4': '50%',
      '3/4': '75%',
      '1/5': '20%',
      '2/5': '40%',
      '3/5': '60%',
      '4/5': '80%',
      '1/6': '16.666667%',
      '2/6': '33.333333%',
      '3/6': '50%',
      '4/6': '66.666667%',
      '5/6': '83.333333%',
      '1/12': '8.333333%',
      '2/12': '16.666667%',
      '3/12': '25%',
      '4/12': '33.333333%',
      '5/12': '41.666667%',
      '6/12': '50%',
      '7/12': '58.333333%',
      '8/12': '66.666667%',
      '9/12': '75%',
      '10/12': '83.333333%',
      '11/12': '91.666667%',
      'button-fix': '140px',
      'form-icon': '30px',
      'organizer-logo': '100px',
      'mobile-button-icon': '40px',
      'button-icon': '24px',
      'live-image': '90px',
      'card-image': '100px',
      menu: '360px',
      'navigation-bar': '70px',
      'thin-modal': '400px',
    }),
  },

  variants: {
    inset: ['responsive', 'focus'],
  },

  corePlugins: {
    container: false,
  },
};
